import { UserPlus, Briefcase, RefreshCw, Send, Mail, Phone, Building2, Lock, RotateCcw, ToggleLeft, ToggleRight, Trash2 } from "lucide-react";
import PropTypes from "prop-types";

/**
 * Shared list/form component used by both the "Venditori" and the
 * "Back-Office" dealer tabs.
 *
 * Zorzutti CR 12 Feb 2026 — the legacy single-checkbox UX was replaced
 * by two top-level tabs. The role flag (`is_back_office`) is now
 * implicit in `mode` and is set by the `useDealerSellers` hook on
 * submit, so no checkbox is rendered.
 *
 * Strings fall back to the previously-used Italian copy so the
 * component keeps working until every translation pack ships the new
 * keys (graceful upgrade).
 */
const DealerSellersView = ({
  mode = "seller",
  sellers, sellerForm, setSellerForm, creatingSeller,
  showSellerForm, setShowSellerForm,
  handleCreateSeller, handleToggleSeller, handleDeleteSeller, handleResendSetup,
  fetchSellers, t,
}) => {
  const isBackOffice = mode === "back_office";

  // Mode-aware copy. Falls back to the original Italian strings if the
  // translation pack hasn't shipped the back-office keys yet.
  const copy = isBackOffice
    ? {
        title: t.backOffice || "Back-Office",
        newBtn: t.newBackOffice || "Nuovo Back-Office",
        addTitle: t.addBackOffice || "Aggiungi Back-Office",
        infoText: t.backOfficeEmailInfo ||
          "L'utenza Back-Office ricevera un'email con il link per creare la sua password. Le utenze Back-Office possono inserire clienti per conto dei venditori e monitorare tutti i contratti, ma non appaiono nel dropdown \"Venditore associato\" ne vedono statistiche di performance.",
        submitBtn: t.createBackOffice || "Crea Back-Office e Invia Email",
        emptyTitle: t.noBackOffice || "Nessuna utenza Back-Office registrata",
        emptyHint: t.clickToAddBackOffice || "Clicca \"Nuovo Back-Office\" per aggiungere personale amministrativo.",
        Icon: Briefcase,
        testidPrefix: "back-office",
      }
    : {
        title: t.sellers,
        newBtn: t.newSeller,
        addTitle: t.addSeller,
        infoText: t.sellerEmailInfo,
        submitBtn: t.createSeller,
        emptyTitle: t.noSellers,
        emptyHint: t.clickToAdd,
        Icon: UserPlus,
        testidPrefix: "seller",
      };
  const Icon = copy.Icon;

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-white" data-testid={`${copy.testidPrefix}s-title`}>
          {copy.title} ({sellers.length})
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => fetchSellers()}
            className="btn-lojack-outline text-sm flex items-center gap-2"
            data-testid={`${copy.testidPrefix}-refresh-btn`}>
            <RefreshCw size={14} /> {t.refresh}
          </button>
          <button
            onClick={() => setShowSellerForm(!showSellerForm)}
            className="btn-lojack text-sm flex items-center gap-2"
            data-testid={`add-${copy.testidPrefix}-btn`}>
            <Icon size={14} /> {showSellerForm ? t.close : copy.newBtn}
          </button>
        </div>
      </div>

      {/* New entry form (Seller OR Back-Office, depending on mode) */}
      {showSellerForm && (
        <div className="wallet-card mb-6" data-testid={`${copy.testidPrefix}-form-card`}>
          <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
            <Icon size={18} className="text-lojack-red" /> {copy.addTitle}
          </h3>
          <p className="text-gray-500 text-sm mb-4">{copy.infoText}</p>
          <form onSubmit={handleCreateSeller} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-xs">{t.firstName} *</label>
                <input value={sellerForm.first_name} onChange={e => setSellerForm({...sellerForm, first_name: e.target.value})} required
                  className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid={`${copy.testidPrefix}-input-firstname`} />
              </div>
              <div>
                <label className="text-gray-400 text-xs">{t.lastName} *</label>
                <input value={sellerForm.last_name} onChange={e => setSellerForm({...sellerForm, last_name: e.target.value})} required
                  className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid={`${copy.testidPrefix}-input-lastname`} />
              </div>
            </div>
            <div>
              <label className="text-gray-400 text-xs">{t.email} *</label>
              <input type="email" value={sellerForm.email} onChange={e => setSellerForm({...sellerForm, email: e.target.value})} required
                className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" data-testid={`${copy.testidPrefix}-input-email`} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-gray-400 text-xs">{t.phoneSeller}</label>
                <input value={sellerForm.phone} onChange={e => setSellerForm({...sellerForm, phone: e.target.value})}
                  className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" placeholder="+39..." data-testid={`${copy.testidPrefix}-input-phone`} />
              </div>
              <div>
                <label className="text-gray-400 text-xs">{t.department}</label>
                <input value={sellerForm.department} onChange={e => setSellerForm({...sellerForm, department: e.target.value})}
                  className="w-full mt-1 px-4 py-3 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-lojack-red focus:outline-none" placeholder={t.salesTeamPlaceholder || "es. Vendite Nord"} data-testid={`${copy.testidPrefix}-input-department`} />
              </div>
            </div>
            <button type="submit" disabled={creatingSeller} className="btn-lojack w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid={`${copy.testidPrefix}-submit`}>
              {creatingSeller ? t.creating : <><Send size={14} /> {copy.submitBtn}</>}
            </button>
          </form>
        </div>
      )}

      {/* List */}
      {sellers.length === 0 ? (
        <div className="wallet-card text-center py-8">
          <Icon size={40} className="text-gray-700 mx-auto mb-3" />
          <p className="text-gray-500">{copy.emptyTitle}</p>
          <p className="text-gray-600 text-sm mt-1">{copy.emptyHint}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sellers.map(s => (
            <div key={s.id} className="wallet-card" data-testid={`${copy.testidPrefix}-card-${s.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-lojack-red/20 to-blue-500/20 border border-gray-700 flex items-center justify-center text-white font-bold text-sm">
                    {s.first_name?.[0]}{s.last_name?.[0]}
                  </div>
                  <div>
                    <p className="text-white font-medium">{s.first_name} {s.last_name}</p>
                    <p className="text-gray-500 text-xs flex items-center gap-2">
                      <Mail size={10} className="inline" />{s.email}
                      {s.phone && <><Phone size={10} className="inline ml-1" />{s.phone}</>}
                      {s.department && <><Building2 size={10} className="inline ml-1" />{s.department}</>}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {!s.password_set && (
                    <button onClick={() => handleResendSetup(s.id)} className="px-2 py-1 text-xs bg-lojack-clause-500/10 text-lojack-clause-400 border border-lojack-clause-500/30 rounded-lg hover:bg-lojack-clause-500/20 transition-colors" title={t.resendActivationEmail || "Re-invia email attivazione"} data-testid={`${copy.testidPrefix}-resend-${s.id}`}>
                      <RotateCcw size={12} className="inline mr-1" />{t.activation}
                    </button>
                  )}
                  <span className={`px-2 py-1 rounded-full text-xs ${s.password_set ? 'bg-lojack-blue-500/20 text-lojack-blue-400' : 'bg-orange-500/20 text-orange-400'}`} data-testid={`${copy.testidPrefix}-status-${s.id}`}>
                    {s.password_set ? <><Lock size={10} className="inline mr-1" />{t.active}</> : t.pending}
                  </span>
                  <button onClick={() => handleToggleSeller(s.id)} className="p-1.5 rounded-lg hover:bg-gray-800 transition-colors" title={s.is_active ? (t.deactivateAction || 'Disattiva') : (t.activateAction || 'Attiva')} data-testid={`${copy.testidPrefix}-toggle-${s.id}`}>
                    {s.is_active ? <ToggleRight size={18} className="text-lojack-blue-400" /> : <ToggleLeft size={18} className="text-gray-600" />}
                  </button>
                  <button onClick={() => handleDeleteSeller(s.id, `${s.first_name} ${s.last_name}`)} className="p-1.5 rounded-lg hover:bg-red-900/30 transition-colors" title={t.deleteAction || "Elimina"} data-testid={`${copy.testidPrefix}-delete-${s.id}`}>
                    <Trash2 size={16} className="text-red-500/60 hover:text-red-400" />
                  </button>
                </div>
              </div>
              {!s.is_active && <p className="text-red-400/60 text-xs mt-2 pl-14">{t.deactivated}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DealerSellersView;

DealerSellersView.propTypes = {
  mode: PropTypes.oneOf(["seller", "back_office"]),
  sellers: PropTypes.array.isRequired,
  sellerForm: PropTypes.object.isRequired,
  setSellerForm: PropTypes.func.isRequired,
  creatingSeller: PropTypes.bool,
  showSellerForm: PropTypes.bool,
  setShowSellerForm: PropTypes.func.isRequired,
  handleCreateSeller: PropTypes.func.isRequired,
  handleToggleSeller: PropTypes.func.isRequired,
  handleDeleteSeller: PropTypes.func.isRequired,
  handleResendSetup: PropTypes.func.isRequired,
  fetchSellers: PropTypes.func.isRequired,
  t: PropTypes.object.isRequired,
};
